import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, History } from "lucide-react";

const brl = (n) => (Number(n || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—";

const STATUS_LABEL = { aberto: "EM ABERTO", andamento: "EM ANDAMENTO", pago: "PAGO" };
const STATUS_STYLE = {
  aberto: "bg-amber-100 text-amber-800 border-amber-200",
  andamento: "bg-sky-100 text-sky-800 border-sky-200",
  pago: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const empty = {
  solicitante_id: "", period_start: "", period_end: "",
  valor_aberto: "0", valor_andamento: "0", valor_pago: "0",
  status: "aberto", data_pagamento: "", observacao: "",
};

export default function FinanceiroTab() {
  const [rows, setRows] = useState([]);
  const [solicitantes, setSolicitantes] = useState([]);
  const [filterSol, setFilterSol] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);

  const load = async () => {
    try {
      const [f, s] = await Promise.all([api.get("/financeiro"), api.get("/solicitantes")]);
      setRows(f.data); setSolicitantes(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditing(null);
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
    setForm({ ...empty, period_start: first, period_end: last });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      solicitante_id: r.solicitante_id,
      period_start: r.period_start, period_end: r.period_end,
      valor_aberto: String(r.valor_aberto ?? 0),
      valor_andamento: String(r.valor_andamento ?? 0),
      valor_pago: String(r.valor_pago ?? 0),
      status: r.status || "aberto",
      data_pagamento: r.data_pagamento || "",
      observacao: r.observacao || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.solicitante_id) { toast.error("Selecione o solicitante"); return; }
    if (!form.period_start || !form.period_end) { toast.error("Informe o período"); return; }
    setSaving(true);
    try {
      const payload = {
        solicitante_id: form.solicitante_id,
        period_start: form.period_start, period_end: form.period_end,
        valor_aberto: Number(form.valor_aberto) || 0,
        valor_andamento: Number(form.valor_andamento) || 0,
        valor_pago: Number(form.valor_pago) || 0,
        status: form.status,
        data_pagamento: form.data_pagamento || null,
        observacao: form.observacao,
      };
      if (editing) await api.put(`/financeiro/${editing.id}`, payload);
      else await api.post("/financeiro", payload);
      toast.success(editing ? "Período atualizado" : "Período criado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Excluir o período ${fmtDate(r.period_start)} - ${fmtDate(r.period_end)}?`)) return;
    try { await api.delete(`/financeiro/${r.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const showAudit = async (r) => {
    try {
      const { data } = await api.get(`/financeiro/${r.id}/audit`);
      setAudit({ row: r, items: data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = filterSol === "__all__" ? rows : rows.filter(r => r.solicitante_id === filterSol);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <CardTitle className="font-heading">Gerenciar financeiro dos solicitantes</CardTitle>
          <CardDescription>Defina período, valores, status e observação para cada solicitante.</CardDescription>
        </div>
        <div className="flex gap-3 items-end">
          <div className="space-y-1 min-w-[220px]">
            <Label className="text-xs">Filtrar por solicitante</Label>
            <Select value={filterSol} onValueChange={setFilterSol}>
              <SelectTrigger data-testid="fin-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {solicitantes.map(s => (<SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="fin-new-btn" onClick={openNew} className="btn-brand rounded-full">
            <Plus className="h-4 w-4 mr-2" /> Novo período
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Solicitante</TableHead>
              <TableHead>Período</TableHead>
              <TableHead className="text-right">Aberto</TableHead>
              <TableHead className="text-right">Andamento</TableHead>
              <TableHead className="text-right">Pago</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead className="w-40">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Nenhum período cadastrado.</TableCell></TableRow>}
              {filtered.map((r, i) => (
                <TableRow key={r.id} className="row-fade" style={{ animationDelay: `${i * 20}ms` }}>
                  <TableCell className="font-medium">{r.solicitante_nome}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(r.period_start)} - {fmtDate(r.period_end)}</TableCell>
                  <TableCell className="text-right font-mono">{brl(r.valor_aberto)}</TableCell>
                  <TableCell className="text-right font-mono">{brl(r.valor_andamento)}</TableCell>
                  <TableCell className="text-right font-mono">{brl(r.valor_pago)}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_STYLE[r.status] || STATUS_STYLE.aberto}>{STATUS_LABEL[r.status] || "EM ABERTO"}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(r.data_pagamento)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`fin-edit-${r.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => showAudit(r)} data-testid={`fin-audit-${r.id}`}><History className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`fin-del-${r.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar período" : "Novo período"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Solicitante</Label>
              <Select value={form.solicitante_id} onValueChange={(v) => set("solicitante_id", v)} disabled={!!editing}>
                <SelectTrigger data-testid="fin-sol-select"><SelectValue placeholder="Escolha o solicitante" /></SelectTrigger>
                <SelectContent>
                  {solicitantes.map(s => (<SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Início do período</Label>
              <Input data-testid="fin-period-start" type="date" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} /></div>
            <div className="space-y-2"><Label>Fim do período</Label>
              <Input data-testid="fin-period-end" type="date" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} /></div>
            <div className="space-y-2"><Label>Valor em aberto (R$)</Label>
              <Input data-testid="fin-aberto" type="number" step="0.01" min="0" value={form.valor_aberto} onChange={(e) => set("valor_aberto", e.target.value)} /></div>
            <div className="space-y-2"><Label>Valor em andamento (R$)</Label>
              <Input data-testid="fin-andamento" type="number" step="0.01" min="0" value={form.valor_andamento} onChange={(e) => set("valor_andamento", e.target.value)} /></div>
            <div className="space-y-2"><Label>Valor pago (R$)</Label>
              <Input data-testid="fin-pago" type="number" step="0.01" min="0" value={form.valor_pago} onChange={(e) => set("valor_pago", e.target.value)} /></div>
            <div className="space-y-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="fin-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aberto">EM ABERTO</SelectItem>
                  <SelectItem value="andamento">EM ANDAMENTO</SelectItem>
                  <SelectItem value="pago">PAGO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Data do pagamento</Label>
              <Input data-testid="fin-data-pag" type="date" value={form.data_pagamento || ""} onChange={(e) => set("data_pagamento", e.target.value)} /></div>
            <div className="space-y-2 col-span-2"><Label>Observação para o solicitante</Label>
              <Textarea data-testid="fin-obs" rows={3} value={form.observacao} onChange={(e) => set("observacao", e.target.value)}
                placeholder="Ex: Pagamento em conferência. Previsão de pagamento até 10/08/2026." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="fin-save" className="btn-brand">
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!audit} onOpenChange={(v) => !v && setAudit(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Histórico de alterações</DialogTitle></DialogHeader>
          {audit && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {audit.items.length === 0 && <div className="text-sm text-slate-500">Nenhuma alteração registrada.</div>}
              {audit.items.map(a => (
                <div key={a.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-xs text-slate-500 mb-2">
                    {new Date(a.changed_at).toLocaleString("pt-BR")} · {a.admin_username}
                  </div>
                  {a.changes.__created__ ? (
                    <div className="text-sm text-emerald-700">Registro criado</div>
                  ) : (
                    <div className="space-y-1 text-sm">
                      {Object.entries(a.changes).map(([k, v]) => (
                        <div key={k} className="font-mono text-xs">
                          <b>{k}:</b> <span className="text-slate-500">{String(v.from ?? "—")}</span> → <span className="text-slate-900">{String(v.to ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
